import { Grid } from '@mui/material';
import { HSpacer, RoutalPalette } from '@nx-smartmonkey/ui';
import React, { useEffect, useState } from 'react';
import PinField from 'react-pin-field';
import styled from 'styled-components';

import { LoaderPage } from '../../../components/LoaderPage';
import Button from '../../../react-components/Button';
import { Text } from '../../../react-components/Text';
import { useSessionGenerateOtp } from '../hooks/useSessionGenerateOtp';

const RESEND_CODE_TIMEOUT_SECONDS = 30;

const ResendButton = ({ onClick }: { onClick: () => void }) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [showCountdown, setShowCountdown] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<number>(RESEND_CODE_TIMEOUT_SECONDS);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (!isVisible && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
        if (countdown - 1 === 0) {
          setIsVisible(true);
          clearTimeout(timer);
          setCountdown(RESEND_CODE_TIMEOUT_SECONDS);
        }
      }, 1000);
    }
    return () => {};
  }, [isVisible, countdown]);

  return (
    <Grid display={`flex`} alignItems={`center`} flexDirection={`column`} marginTop={`0px`}>
      <Button
        variant="text"
        style={{
          margin: 0,
          padding: 0,
          border: `none`,
          display: `flex`,
          opacity: isVisible ? 1 : 0.4,
          cursor: isVisible ? `pointer` : `default`,
          fontWeight: 200,
        }}
        onClick={() => {
          setShowCountdown(true);
          if (!isVisible) return;
          setIsVisible(false);
          setCountdown(RESEND_CODE_TIMEOUT_SECONDS);
          onClick();
        }}
      >
        <HSpacer small />
        <Text
          variant="button"
          intlId="auth.resend_code"
          style={{
            cursor: isVisible ? `pointer` : `default`,
          }}
        />
      </Button>
      {showCountdown && (
        <Text
          style={{
            fontSize: `13px`,
            color: RoutalPalette.secondary40,
            margin: 0,
            padding: 0,
            display: isVisible ? `none` : `block`,
            height: `fit-content`,
            fontWeight: 200,
          }}
        >
          {countdown}s
        </Text>
      )}
    </Grid>
  );
};

const StyledPinField = styled(PinField)`
  flex-direction: row;
  align-content: space-around;
  justify-content: center;
  flex-wrap: nowrap;
  width: 32px;
  height: 50px;
  font-size: 22px;
  font-weight: 600;
  color: ${RoutalPalette.secondary40};
  user-select: none;
  border-radius: 10px;
  border: 1px solid ${RoutalPalette.primary20};
  text-align: center;

  :focus {
    outline: none;
    box-shadow: inset ${RoutalPalette.primary40} 0px 0px 0px 2px !important;
    border-radius: 10px;
  }

  @media (max-width: 320px) {
    margin-right: 1px;
    margin-left: 1px;
    width: 27px;
  }
  @media (max-width: 270px) {
    margin-right: 1px;
    margin-left: 1px;
    width: 20px;
  }

  :nth-of-type(1) {
    margin-left: 0px;
  }
  :nth-last-of-type(1) {
    margin-right: 0px;
  }
`;

interface OTPFormProps {
  phone: string;
  isLoading: boolean;
  isError: boolean;
  validate: (otp: string) => void;
}

export const OTPForm = ({ phone, isLoading, isError, validate }: OTPFormProps) => {
  const [otp, setOtp] = useState<string>(``);
  const { run: generateOtp, isLoading: isLoadingOtp } = useSessionGenerateOtp();

  useEffect(() => {
    if (otp.length === 6) {
      validate(otp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  useEffect(() => {
    if (isError) {
      setOtp(``);
    }
  }, [isError]);

  const handlePaste: React.ClipboardEventHandler = (event) => {
    const data = event.clipboardData.getData(`text`);
    setOtp(data);
  };

  // Loading page para Auth
  if (isLoading || isLoadingOtp) {
    return (
      <Grid container flexDirection="column" alignItems="center" style={{ height: `100%` }}>
        <LoaderPage />
      </Grid>
    );
  }

  return (
    <Grid container flexDirection="column" alignItems="center" style={{ width: `100%` }} gap="24px">
      <Text
        intlId="drivers.auth.otp.title"
        variant="h5"
        style={{ textAlign: `center`, color: RoutalPalette.primary60 }}
      />
      <Grid item container direction="column" gap="12px">
        <Grid item container direction="row" style={{ justifyContent: `space-evenly`, width: `100%` }}>
          <StyledPinField
            className="pin-field"
            pattern="[0-9]*"
            inputMode="numeric"
            onPaste={handlePaste}
            onChange={setOtp}
            autoComplete="one-time-code"
            length={6}
            autoFocus
            style={{ height: `45px` }}
          />
        </Grid>

        <Grid
          container
          direction="row"
          alignItems="center"
          justifyContent="center"
          style={{
            height: `42px`,
          }}
        >
          <ResendButton onClick={() => generateOtp({ phone })} />
        </Grid>
      </Grid>
    </Grid>
  );
};
